/**
 * 未対応pt カード（当月pt カードの上に配置する小カード）
 * 未対応（status=未対応・親チケットあり）子チケットの予定pt 合計をピクセルフォントで
 * `値 pt` 形式で表示する。数字色・hover 背景は淡いピンク (--pt-accent: #eb838c)。
 * 数字ボタンのクリックで未対応チケット明細モーダルを開く。月選択は持たない（現在スナップショットのみ）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー個人の未対応pt（未対応子チケットの予定pt 合計）カード。クリックで明細モーダル
 * @graph-connects useMemberOpenPoints [calls] 未対応pt 明細フック（合計値の表示）
 * @graph-connects OpenPtDetailModal [calls] 未対応pt 明細モーダル
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { useMemberOpenPoints } from '../../hooks/useMember.js';
import { OpenPtDetailModal } from './OpenPtDetailModal.js';
import { formatNum } from './combat-power/combat-power-utils.js';

/** @graph-connects none */
interface OpenPtCardProps {
  /** メンバーのメールアドレス */
  email: string;
}

/**
 * 未対応pt カードの主題色（淡いピンク）。current-month-pt-card の hover 反転にも使われる
 * @graph-connects none
 */
const OPEN_PT_ACCENT_STYLE = { '--pt-accent': '#eb838c' } as CSSProperties;

/** @graph-connects useMemberOpenPoints [calls] 未対応pt 明細フック */
export function OpenPtCard({ email }: OpenPtCardProps) {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const { data } = useMemberOpenPoints(email);

  const totalPoint = data?.totalPoint ?? null;

  return (
    <>
      <Card
        className="current-month-pt-card animate-content-reveal flex flex-col md:w-1/4 md:min-w-[180px]"
        style={OPEN_PT_ACCENT_STYLE}
      >
        <CardHeader title={<span data-card-text>{t('members.quarterlyKpi.openPt')}</span>} />
        <CardContent className="flex flex-1 p-0">
          <button
            type="button"
            data-testid="open-pt-card"
            aria-label={t('members.quarterlyKpi.openPtDetail.openLabel')}
            onClick={() => {
              setDetailOpen(true);
            }}
            className="pt-open-button flex flex-1 flex-col items-center justify-center gap-2 py-3"
          >
            <div
              className="pt-number text-center leading-none"
              style={{ fontFamily: "'Press Start 2P', monospace" }}
              data-testid="open-pt-value"
            >
              <span className="text-3xl xl:text-4xl text-[color:var(--pt-accent)]">
                {totalPoint === null ? '-' : formatNum(totalPoint, 1)}
              </span>
              <span data-card-text className="ml-2 text-sm text-[var(--color-text-secondary)]">
                pt
              </span>
            </div>
            <p data-card-text className="text-[11px] text-[var(--color-text-muted)]">
              {t('members.quarterlyKpi.openPtNote')}
            </p>
          </button>
        </CardContent>
      </Card>
      <OpenPtDetailModal
        email={email}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
        }}
      />
    </>
  );
}
